import * as React from 'react'
import * as SliderPrimitive from '@radix-ui/react-slider'

import { cn } from '@/utils'

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    /** Accessible name for the Thumb (which carries Radix's `role="slider"`) — Root itself has no
     *  ARIA role, so an `aria-label` there is never read by a screen reader. */
    thumbLabel?: string
    /** Overrides the Thumb's automatic `aria-valuenow` announcement (e.g. "50") with descriptive
     *  text — for states a bare number would misrepresent, like a mixed-value selection. Omit to
     *  keep Radix's default numeric announcement. */
    thumbValueText?: string
    /** Extra classes for the Track (e.g. a design-specific track color). */
    trackClassName?: string
    /** Extra classes for the Thumb (e.g. a design-specific border color). */
    thumbClassName?: string
  }
>(({ className, thumbLabel, thumbValueText, trackClassName, thumbClassName, ...props }, ref) => (
  <SliderPrimitive.Root
    ref={ref}
    data-slot="slider"
    // Radix's Root/Thumb aren't native disableable form elements, so they never carry the real
    // `disabled` DOM property — the `:disabled` pseudo-class `disabled:opacity-50` targets (see the
    // Thumb below) can never match, and a disabled slider rendered with zero visual difference from
    // an enabled one. Radix does set `data-disabled` on Root when the `disabled` prop is true, which
    // this DOES match; dimming the whole Root dims Track/Range/Thumb together via ordinary opacity
    // compositing, so those inner elements need no changes of their own.
    className={cn(
      'relative flex w-full touch-none select-none items-center data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
      className
    )}
    {...props}
  >
    <SliderPrimitive.Track
      data-slot="slider-track"
      className={cn(
        'relative h-1.5 w-full grow overflow-hidden rounded-full bg-primary/20',
        trackClassName
      )}
    >
      <SliderPrimitive.Range data-slot="slider-range" className="absolute h-full bg-primary" />
    </SliderPrimitive.Track>
    <SliderPrimitive.Thumb
      data-slot="slider-thumb"
      aria-label={thumbLabel}
      aria-valuetext={thumbValueText}
      className={cn(
        'block h-4 w-4 rounded-full border border-primary/50 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
        thumbClassName
      )}
    />
  </SliderPrimitive.Root>
))

Slider.displayName = SliderPrimitive.Root.displayName

export { Slider }
